/**
 * Logica de dominio GTD.
 * Constantes (contextos, pasos de revision) y operaciones puras sobre items.
 */

import type { Category, Context, GtdItem, Priority } from './types';
import { genId } from './db';

export const CONTEXTS: { id: Context; label: string; icon: string }[] = [
  { id: '@computadora', label: '@computadora', icon: '💻' },
  { id: '@telefono', label: '@telefono', icon: '📱' },
  { id: '@calle', label: '@calle', icon: '🚶' },
  { id: '@oficina', label: '@oficina', icon: '🏢' },
  { id: '@casa', label: '@casa', icon: '🏠' },
  { id: '@whatsapp', label: '@whatsapp', icon: '💬' },
  { id: '@email', label: '@email', icon: '📧' }
];

export const REVIEW_STEPS = [
  {
    id: 'r1',
    title: 'Vaciar la cabeza',
    desc: 'Capturá en el Inbox todo lo que tenés pendiente: ideas, compromisos, preocupaciones. Todo. Sin filtrar.'
  },
  {
    id: 'r2',
    title: 'Procesar el Inbox',
    desc: 'Revisá cada item: ¿es accionable? Usá el botón Procesar para clasificar cada uno.'
  },
  {
    id: 'r3',
    title: 'Revisar Próximas Acciones',
    desc: '¿Las completaste? ¿Siguen siendo relevantes? Marcá las hechas y eliminá las que ya no importan.'
  },
  {
    id: 'r4',
    title: 'Revisar Proyectos',
    desc: '¿Cada proyecto tiene al menos 1 próxima acción? ¿Completaste o abandonaste alguno?'
  },
  {
    id: 'r5',
    title: 'Revisar Esperando',
    desc: '¿Recibiste lo que esperabas? ¿Necesitás hacer follow-up? Actualizá estados.'
  },
  {
    id: 'r6',
    title: 'Revisar Algún Día / Tal Vez',
    desc: '¿Algo que ahora sí querés hacer? Movelo a Acción. ¿Algo que ya no te interesa? Eliminalo.'
  },
  {
    id: 'r7',
    title: 'Planificar la semana',
    desc: 'Mirá tus Próximas Acciones. ¿Cuáles son las 3 a 5 más importantes para esta semana?'
  }
];

export const CATEGORY_LABELS: Record<Category, string> = {
  inbox: 'Inbox',
  next: 'Próximas Acciones',
  project: 'Proyectos',
  waiting: 'Esperando',
  someday: 'Algún Día',
  reference: 'Referencia'
};

export interface NewItemInput {
  text: string;
  category?: Category;
  context?: Context;
  projectId?: string;
  priority?: Priority;
  notes?: string;
  waitingWho?: string;
  imageId?: string;
}

export function makeItem(input: NewItemInput): GtdItem {
  const now = Date.now();
  return {
    id: genId(),
    text: input.text.trim(),
    category: input.category ?? 'inbox',
    context: input.context ?? '',
    projectId: input.projectId ?? '',
    priority: input.priority ?? 'normal',
    notes: input.notes ?? '',
    waitingWho: input.waitingWho ?? '',
    imageId: input.imageId ?? '',
    done: false,
    doneAt: null,
    createdAt: now,
    updatedAt: now,
    deleted: false
  };
}

export function touchItem(item: GtdItem, patch: Partial<GtdItem>): GtdItem {
  return {
    ...item,
    ...patch,
    updatedAt: Date.now()
  };
}

export function markDone(item: GtdItem): GtdItem {
  const now = Date.now();
  return { ...item, done: true, doneAt: now, updatedAt: now };
}

export function restore(item: GtdItem): GtdItem {
  return { ...item, done: false, doneAt: null, updatedAt: Date.now() };
}

export function softDelete(item: GtdItem): GtdItem {
  return { ...item, deleted: true, updatedAt: Date.now() };
}

// ---- Filters ----

export function active(items: GtdItem[], cat?: Category): GtdItem[] {
  return items.filter((i) => !i.deleted && !i.done && (!cat || i.category === cat));
}

export function done(items: GtdItem[]): GtdItem[] {
  return items
    .filter((i) => !i.deleted && i.done)
    .sort((a, b) => (b.doneAt ?? 0) - (a.doneAt ?? 0));
}

export function byContext(items: GtdItem[], ctx: Context): GtdItem[] {
  return active(items, 'next').filter((i) => i.context === ctx);
}

export function projectActions(items: GtdItem[], projectId: string): GtdItem[] {
  return items.filter((i) => i.projectId === projectId && !i.deleted);
}

export function counts(items: GtdItem[]): Record<Category | 'doneTotal' | 'total', number> {
  const acc = {
    inbox: 0,
    next: 0,
    project: 0,
    waiting: 0,
    someday: 0,
    reference: 0,
    doneTotal: 0,
    total: 0
  };
  for (const i of items) {
    if (i.deleted) continue;
    acc.total++;
    if (i.done) {
      acc.doneTotal++;
    } else {
      acc[i.category]++;
    }
  }
  return acc;
}

// ---- Date formatting ----

export function formatDate(ts: number): string {
  const d = new Date(ts);
  const now = Date.now();
  const diff = now - ts;
  if (diff < 60_000) return 'Ahora';
  if (diff < 3_600_000) return Math.floor(diff / 60_000) + ' min';
  if (diff < 86_400_000) return Math.floor(diff / 3_600_000) + 'h';
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}
